import {useState, useEffect, useCallback, useRef} from 'react';
import {urlApiDataCRGCI, apiKeyDataCRGCI} from '../helper/api';

interface Position {
  id: string;
  x: number;
  y: number;
}

const useInitiativePositions = () => {
  const [positions, setPositions] = useState<Position[]>([]);
  const [canEdit, setCanEdit] = useState(false);
  const permissionChecked = useRef(false);

  const checkCanEdit = useCallback(async (jwt: string, documentId: string) => {
    if (permissionChecked.current) return;
    permissionChecked.current = true;
    try {
      const res = await fetch(`${urlApiDataCRGCI}/api/initiatives/${documentId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${jwt}`,
        },
        body: JSON.stringify({data: {}}),
      });
      setCanEdit(res.ok);
    } catch {
      setCanEdit(false);
    }
  }, []);

  const loadPositions = useCallback(async () => {
    if (!urlApiDataCRGCI || !apiKeyDataCRGCI) return;
    try {
      const url = new URL(`${urlApiDataCRGCI}/api/initiatives`);
      url.searchParams.set('fields[0]', 'documentId');
      url.searchParams.set('fields[1]', 'x_position');
      url.searchParams.set('fields[2]', 'y_position');
      url.searchParams.set('pagination[pageSize]', '500');

      const response = await fetch(url.href, {
        headers: {Authorization: `Bearer ${apiKeyDataCRGCI}`},
      });
      const data = await response.json();

      const loaded: Position[] = (data.data || [])
        .filter((item: any) => item.x_position != null && item.y_position != null)
        .map((item: any) => ({
          id: item.documentId,
          x: item.x_position,
          y: item.y_position,
        }));

      setPositions(loaded);

      const jwt = localStorage.getItem('jwtRadar');
      if (jwt && data.data?.length > 0) {
        await checkCanEdit(jwt, data.data[0].documentId);
      } else {
        setCanEdit(false);
      }
    } catch {}
  }, [checkCanEdit]);

  useEffect(() => {
    loadPositions();
  }, [loadPositions]);

  useEffect(() => {
    const onAuthChanged = () => {
      permissionChecked.current = false;
      loadPositions();
    };

    window.addEventListener('authChanged', onAuthChanged);
    return () => {
      window.removeEventListener('authChanged', onAuthChanged);
    };
  }, [loadPositions]);

  const savePosition = useCallback(async (documentId: string | number, x: number, y: number) => {
    const jwt = localStorage.getItem('jwtRadar');
    if (!jwt || !urlApiDataCRGCI) return;

    setPositions(prev => {
      const existing = prev.find(p => p.id === documentId);
      if (existing) {
        return prev.map(p => p.id === documentId ? {...p, x, y} : p);
      }
      return [...prev, {id: String(documentId), x, y}];
    });

    try {
      await fetch(`${urlApiDataCRGCI}/api/initiatives/${documentId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${jwt}`,
        },
        body: JSON.stringify({data: {x_position: x, y_position: y}}),
      });
    } catch {}
  }, []);

  return {positions, savePosition, canEdit};
};

export default useInitiativePositions;
